import styled from 'styled-components';

import { useDebouncedValue } from '@components/component-helpers';
import { EmbedVideo } from '@components/embed-video';
import { IconVideo } from '@components/icons/video';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';

type Config = FieldConfig<'video'>;

const Display: Config['Display'] = ({ field }) => {
  return field.url ? <EmbedVideo url={field.url} /> : <Fallback showText />;
};

const FormBuilderVideo: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;
  const delayedUrl = useDebouncedValue(field.url, 1000);

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        display={<Display field={field} />}
        edit={
          <>
            {delayedUrl ? <EmbedVideo url={delayedUrl} /> : <Fallback />}
            <TextField
              onChange={actions.createChangeHandler(field, 'url')}
              value={field.url}
              label={t('url')}
              placeholder="https://"
            />
          </>
        }
      />
    </FieldDropzone>
  );
};

const Preview: Config['TreeDisplay'] = () => {
  return <Text text={t('title')} font="regular14" />;
};

const Fallback = ({ showText = false }: { showText?: boolean }) => (
  <FallbackContainer>
    <IconVideo size={40} color="purple50" />
    {showText && <Text font="regular14" color="purple50" text={t('noVideo')} />}
  </FallbackContainer>
);

const { t } = getScopedTranslate({
  en: {
    title: 'Video',
    url: 'URL',
    noVideo: 'No video added',
  },
});

export const videoConfig: Config = {
  kind: 'video',
  title: t('title'),
  group: 'content',
  propertiesTabs: [conditionsTab],
  Icon: IconVideo,
  Component: FormBuilderVideo,
  Display,
  TreeDisplay: Preview,
};

const FallbackContainer = styled.div({
  borderRadius: pxToRem(8),
  background: theme.color.uiTrans5,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: pxToRem(16),
});
